import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI } from '../api/auth';
function extractAuthErrorMessage(error, fallback) {
    const responseData = error?.response?.data;
    const retryAfter = responseData?.error?.retryAfterSeconds;
    const retryMessage = typeof retryAfter === 'number' && retryAfter > 0
        ? ` Try again in ${retryAfter}s.`
        : '';
    if (typeof responseData?.error?.message === 'string') {
        return `${responseData.error.message}${retryMessage}`;
    }
    if (typeof responseData?.message === 'string') {
        return responseData.message;
    }
    if (typeof responseData === 'string' && responseData.trim()) {
        return responseData;
    }
    if (typeof error?.message === 'string' && error.message.trim()) {
        return error.message;
    }
    return fallback;
}
export const useAuthStore = create()(persist((set) => ({
    isAuthenticated: false,
    token: null,
    tenant: null,
    isLoading: false,
    error: null,
    pendingTwoFactor: null,
    pendingRegistration: null,
    async register(email, password, tier = 'DEMO', billing) {
        set({ isLoading: true, error: null });
        try {
            const response = await authAPI.register({
                email,
                password,
                tier,
                firstName: billing?.firstName || '',
                lastName: billing?.lastName || '',
                companyName: billing?.companyName || '',
            });
            if (response.requiresRegistrationVerification && response.pendingRegistrationId) {
                set({
                    isAuthenticated: false,
                    token: null,
                    tenant: null,
                    pendingRegistration: {
                        pendingRegistrationId: response.pendingRegistrationId,
                        emailMasked: response.emailMasked,
                    },
                    isLoading: false,
                });
                return;
            }
            set({
                isAuthenticated: true,
                token: response.token,
                tenant: response.tenant,
                isLoading: false,
                pendingTwoFactor: null,
                pendingRegistration: null,
            });
            localStorage.setItem('auth_token', response.token);
        }
        catch (error) {
            set({
                error: extractAuthErrorMessage(error, 'Registration failed'),
                isLoading: false,
            });
            throw error;
        }
    },
    async login(email, password) {
        set({ isLoading: true, error: null });
        try {
            const response = await authAPI.login({ email, password });
            if (response.requiresTwoFactor && response.challengeId) {
                set({
                    isAuthenticated: false,
                    token: null,
                    tenant: null,
                    pendingTwoFactor: {
                        email,
                        challengeId: response.challengeId,
                        emailMasked: response.emailMasked,
                    },
                    isLoading: false,
                });
                return;
            }
            if (!response.token) {
                throw new Error('No token in response');
            }
            localStorage.setItem('auth_token', response.token);
            set({
                isAuthenticated: true,
                token: response.token,
                tenant: response.tenant,
                isLoading: false,
                pendingTwoFactor: null,
            });
        }
        catch (error) {
            set({
                error: extractAuthErrorMessage(error, 'Login failed'),
                isLoading: false,
            });
            throw error;
        }
    },
    async verifyTwoFactorLogin(code, method = 'totp') {
        const pending = useAuthStore.getState().pendingTwoFactor;
        if (!pending) {
            throw new Error('No pending two-factor challenge');
        }
        set({ isLoading: true, error: null });
        try {
            const response = await authAPI.verifyTwoFactorLogin({
                email: pending.email,
                challengeId: pending.challengeId,
                code,
                method,
            });
            if (!response.token) {
                throw new Error('No token in response');
            }
            localStorage.setItem('auth_token', response.token);
            set({
                isAuthenticated: true,
                token: response.token,
                tenant: response.tenant,
                pendingTwoFactor: null,
                isLoading: false,
            });
        }
        catch (error) {
            set({
                error: extractAuthErrorMessage(error, 'Two-factor verification failed'),
                isLoading: false,
            });
            throw error;
        }
    },
    async verifyRegistration(code) {
        const pending = useAuthStore.getState().pendingRegistration;
        if (!pending) {
            throw new Error('No pending registration request');
        }
        set({ isLoading: true, error: null });
        try {
            const response = await authAPI.verifyRegistration({
                pendingRegistrationId: pending.pendingRegistrationId,
                code,
            });
            if (!response.token) {
                throw new Error('No token in response');
            }
            localStorage.setItem('auth_token', response.token);
            set({
                isAuthenticated: true,
                token: response.token,
                tenant: response.tenant,
                pendingRegistration: null,
                pendingTwoFactor: null,
                isLoading: false,
            });
        }
        catch (error) {
            set({
                error: extractAuthErrorMessage(error, 'Registration verification failed'),
                isLoading: false,
            });
            throw error;
        }
    },
    async resendRegistrationCode() {
        const pending = useAuthStore.getState().pendingRegistration;
        if (!pending) {
            throw new Error('No pending registration request');
        }
        set({ isLoading: true, error: null });
        try {
            await authAPI.resendRegistrationCode({
                pendingRegistrationId: pending.pendingRegistrationId,
            });
            set({ isLoading: false });
        }
        catch (error) {
            set({
                error: extractAuthErrorMessage(error, 'Failed to resend verification code'),
                isLoading: false,
            });
            throw error;
        }
    },
    async resendTwoFactorCode() {
        const pending = useAuthStore.getState().pendingTwoFactor;
        if (!pending) {
            throw new Error('No pending two-factor challenge');
        }
        set({ isLoading: true, error: null });
        try {
            await authAPI.resendTwoFactorCode({
                email: pending.email,
                challengeId: pending.challengeId,
            });
            set({ isLoading: false });
        }
        catch (error) {
            set({
                error: extractAuthErrorMessage(error, 'Failed to resend code'),
                isLoading: false,
            });
            throw error;
        }
    },
    async refreshSession() {
        const existingToken = localStorage.getItem('auth_token');
        if (!existingToken) {
            return;
        }
        try {
            const tenant = await authAPI.getMe();
            set((state) => ({
                ...state,
                isAuthenticated: true,
                token: existingToken,
                tenant: {
                    id: tenant.id,
                    email: tenant.email,
                    tier: tenant.tier,
                    isAdmin: tenant.isAdmin,
                    mustChangePassword: tenant.mustChangePassword,
                    mustSetupTwoFactor: tenant.mustSetupTwoFactor,
                    twoFactorEnabled: tenant.twoFactorEnabled,
                    firstName: tenant.firstName,
                    lastName: tenant.lastName,
                },
            }));
        }
        catch {
            set({
                isAuthenticated: false,
                token: null,
                tenant: null,
                error: null,
            });
            localStorage.removeItem('auth_token');
        }
    },
    async changePassword(newPassword, currentPassword) {
        set({ isLoading: true, error: null });
        try {
            const response = await authAPI.changePassword({ newPassword, currentPassword });
            set((state) => ({
                ...state,
                tenant: state.tenant
                    ? {
                        ...state.tenant,
                        mustChangePassword: response.mustChangePassword,
                        mustSetupTwoFactor: response.mustSetupTwoFactor,
                        twoFactorEnabled: response.twoFactorEnabled,
                    }
                    : state.tenant,
                isLoading: false,
            }));
        }
        catch (error) {
            set({
                error: extractAuthErrorMessage(error, 'Password update failed'),
                isLoading: false,
            });
            throw error;
        }
    },
    async verifyTwoFactorSetup(code) {
        set({ isLoading: true, error: null });
        try {
            const response = await authAPI.verifyTwoFactorSetup({ code });
            set((state) => ({
                ...state,
                tenant: state.tenant
                    ? {
                        ...state.tenant,
                        mustSetupTwoFactor: response.mustSetupTwoFactor,
                        twoFactorEnabled: response.twoFactorEnabled,
                    }
                    : state.tenant,
                isLoading: false,
            }));
        }
        catch (error) {
            set({
                error: extractAuthErrorMessage(error, 'Two-factor setup failed'),
                isLoading: false,
            });
            throw error;
        }
    },
    async resetTwoFactor(currentPassword) {
        set({ isLoading: true, error: null });
        try {
            const response = await authAPI.resetTwoFactor({ currentPassword });
            set((state) => ({
                ...state,
                tenant: state.tenant
                    ? {
                        ...state.tenant,
                        mustSetupTwoFactor: response.mustSetupTwoFactor,
                        twoFactorEnabled: response.twoFactorEnabled,
                    }
                    : state.tenant,
                isLoading: false,
            }));
        }
        catch (error) {
            set({
                error: extractAuthErrorMessage(error, 'Two-factor reset failed'),
                isLoading: false,
            });
            throw error;
        }
    },
    logout() {
        set({
            isAuthenticated: false,
            token: null,
            tenant: null,
            pendingTwoFactor: null,
            pendingRegistration: null,
            error: null,
        });
        localStorage.removeItem('auth_token');
    },
    setError(error) {
        set({ error });
    },
}), {
    name: 'auth-store',
    partialize: (state) => ({
        token: state.token,
        tenant: state.tenant,
        isAuthenticated: state.isAuthenticated,
        pendingRegistration: state.pendingRegistration,
        pendingTwoFactor: state.pendingTwoFactor,
    }),
}));
